"use client";

import { useAuth } from "@clerk/nextjs";
import * as Sentry from "@sentry/nextjs";
import { MutationCache, QueryCache, QueryClient, QueryClientProvider } from "@tanstack/react-query";
import { useRouter } from "next/navigation";
import { useEffect, useState } from "react";
import { ApiError } from "@/lib/api-client";

const SENTRY_CAPTURED_FLAG = "__sentry_captured__";

type RouterLike = ReturnType<typeof useRouter>;
type SignOutLike = ReturnType<typeof useAuth>["signOut"];

// Module-level handles kept in sync via the provider's effect. The
// QueryClient is constructed once but its onError callbacks need to reach
// the *current* router + signOut. We previously used refs, which tripped
// react-hooks/refs because the lazy initialiser read `.current` from
// closures created during render.
let activeRouter: RouterLike | null = null;
let activeSignOut: SignOutLike | null = null;

function isCapturedAlready(error: unknown): boolean {
  return (
    typeof error === "object" &&
    error !== null &&
    (error as { [SENTRY_CAPTURED_FLAG]?: boolean })[SENTRY_CAPTURED_FLAG] === true
  );
}

function redirectToSignOut() {
  const router = activeRouter;
  const signOut = activeSignOut;
  if (!router) return;
  if (signOut) {
    signOut()
      .then(() => router.replace("/"))
      .catch(() => router.replace("/"));
  } else {
    router.replace("/");
  }
}

function createQueryClient(): QueryClient {
  return new QueryClient({
    queryCache: new QueryCache({
      onError: (error, query) => {
        if (error instanceof ApiError && error.status === 401) {
          redirectToSignOut();
          return;
        }
        // ApiError 5xx is already captured by ApiClient; user-fault 4xx
        // is intentionally skipped. Capture only non-ApiError exceptions
        // (typically a JS error inside a queryFn).
        if (!(error instanceof ApiError) && !isCapturedAlready(error)) {
          Sentry.captureException(error, {
            tags: { service: "internal-dashboard", source: "react_query_query" },
            extra: { queryKey: JSON.stringify(query.queryKey) },
          });
        }
      },
    }),
    mutationCache: new MutationCache({
      onError: (error, _variables, _context, mutation) => {
        if (error instanceof ApiError && error.status === 401) {
          redirectToSignOut();
          return;
        }
        if (!(error instanceof ApiError) && !isCapturedAlready(error)) {
          Sentry.captureException(error, {
            tags: { service: "internal-dashboard", source: "react_query_mutation" },
            extra: { mutationKey: JSON.stringify(mutation.options.mutationKey) },
          });
        }
      },
    }),
    defaultOptions: {
      queries: {
        staleTime: 30_000,
        retry: (failureCount, error) => {
          if (error instanceof ApiError && error.status === 401) {
            return false;
          }
          return failureCount < 3;
        },
      },
      mutations: {
        // Mutations are not idempotent by default; never retry. We still
        // short-circuit on 401 to keep the contract explicit.
        retry: (_failureCount, error) => {
          if (error instanceof ApiError && error.status === 401) {
            return false;
          }
          return false;
        },
      },
    },
  });
}

export function ReactQueryProvider({ children }: { children: React.ReactNode }) {
  const router = useRouter();
  const { signOut } = useAuth();

  useEffect(() => {
    activeRouter = router;
    activeSignOut = signOut;
  }, [router, signOut]);

  const [queryClient] = useState(createQueryClient);

  return (
    <QueryClientProvider client={queryClient}>
      {children}
      {/* TODO: Uncomment this when we have a way to hide the devtools */}
      {/* <ReactQueryDevtools initialIsOpen={false} /> */}
    </QueryClientProvider>
  );
}
